<template>
    <BaseWidget v-bind="$attrs" :widget="meta" :loading="loading">
        <div class="dw-storage-body">
            <div class="dw-storage-ring-section">
                <svg class="dw-ring-svg" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="var(--bs-gray-300)" stroke-width="10" class="dw-ring-bg" />
                    <circle cx="60" cy="60" r="50" fill="none" stroke="#3B82F6" stroke-width="10"
                        :stroke-dasharray="dash" stroke-dashoffset="0" stroke-linecap="round" transform="rotate(-90 60 60)" />
                    <text x="60" y="55" text-anchor="middle" fill="var(--bs-body-color)" font-size="22" font-weight="700">%{{ percent }}</text>
                    <text x="60" y="72" text-anchor="middle" fill="var(--bs-gray-500)" font-size="11">{{ $t('common.dashboard.widgetLabels.ringStorage') }}</text>
                </svg>
                <div class="dw-ring-details">
                    <div class="dw-ring-detail">
                        <span class="dw-ring-detail-lbl">{{ $t('common.dashboard.widgetLabels.total') }}</span>
                        <span class="dw-ring-detail-val">{{ apiData?.total_text || '—' }}</span>
                    </div>
                    <div class="dw-ring-detail-divider"></div>
                    <div class="dw-ring-detail">
                        <span class="dw-ring-detail-lbl">{{ $t('common.dashboard.widgetLabels.used') }}</span>
                        <span class="dw-ring-detail-val" style="color: #3B82F6">{{ apiData?.used_text || '—' }}</span>
                    </div>
                    <div class="dw-ring-detail-divider"></div>
                    <div class="dw-ring-detail">
                        <span class="dw-ring-detail-lbl">{{ $t('common.dashboard.widgetLabels.remaining') }}</span>
                        <span class="dw-ring-detail-val" style="color: var(--bs-success)">{{ remainingText }}</span>
                    </div>
                </div>
            </div>
            <div v-if="breakdown.length" class="dw-storage-breakdown">
                <div class="dw-storage-breakdown-row" v-for="(item, i) in breakdown" :key="item.type || item.label">
                    <div class="dw-storage-breakdown-head">
                        <span class="dw-storage-breakdown-label">{{ labelFor(item) }}</span>
                        <span class="dw-storage-breakdown-val">{{ item.text }}</span>
                    </div>
                    <div class="dw-storage-bar-track">
                        <div class="dw-storage-bar-fill" :style="{ width: percentOf(item) + '%', background: colorFor(i) }"></div>
                    </div>
                </div>
            </div>
        </div>
    </BaseWidget>
</template>

<script>
import BaseWidget from '../BaseWidget.vue';
import widgetMixin from '../widgetMixin';

export const meta = {
    id: 'storage',
    titleKey: 'common.dashboard.widgetTitles.storage',
    icon: 'bi-hdd',
    accent: '#3B82F6',
    accentBg: 'rgba(59, 130, 246, 0.12)',
    glassRgb: '59, 130, 246',
    defaultCol: 1,
    defaultRow: 1,
    source: { type: 'statistics', key: 'storage' },
};

const TYPE_LABEL_KEYS = {
    MODULE_VEHICLE_WORK_ORDER: 'common.dashboard.widgetLabels.storageTypeWorkOrder',
    MODULE_NOTE: 'common.dashboard.widgetLabels.storageTypeNote',
    MODULE_MUSIC: 'common.dashboard.widgetLabels.storageTypeMusic',
    MODULE_MUSIC_PLAYLIST: 'common.dashboard.widgetLabels.storageTypeMusicPlaylist',
    FILE_MANAGER_FOLDER: 'common.dashboard.widgetLabels.storageTypeFileManager',
    unattached: 'common.dashboard.widgetLabels.storageTypeUnattached',
};

const COLORS = ['#3B82F6', 'var(--bs-success)', 'var(--bs-danger)', 'var(--bs-warning)', 'var(--bs-info)', '#6f42c1', '#fd7e14', '#20c997', '#d63384', 'var(--bs-gray-500)'];

function bytesToText(b) {
    const n = parseFloat(b) || 0;
    if (n >= 1024 * 1024 * 1024) return (n / 1024 / 1024 / 1024).toFixed(2).replace(/\.?0+$/, '') + ' GB';
    if (n >= 1024 * 1024) return (n / 1024 / 1024).toFixed(2).replace(/\.?0+$/, '') + ' MB';
    if (n >= 1024) return (n / 1024).toFixed(2).replace(/\.?0+$/, '') + ' KB';
    return n + ' B';
}

export default {
    name: 'StorageWidget',
    components: { BaseWidget },
    mixins: [widgetMixin],
    data() {
        return { meta };
    },
    computed: {
        percent() {
            return Math.round(this.apiData?.usage_percent || 0);
        },
        remainingText() {
            return bytesToText(this.apiData?.remaining_bytes);
        },
        breakdown() {
            return Array.isArray(this.apiData?.breakdown) ? this.apiData.breakdown : [];
        },
        dash() {
            const circ = 2 * Math.PI * 50;
            const filled = circ * (this.percent / 100);
            return `${filled} ${circ - filled}`;
        },
    },
    methods: {
        labelFor(item) {
            const key = TYPE_LABEL_KEYS[item.type] || TYPE_LABEL_KEYS[item.label];
            if (key) return this.$t(key);
            return item.label || '—';
        },
        colorFor(i) {
            return COLORS[i % COLORS.length];
        },
        percentOf(item) {
            const total = parseFloat(this.apiData?.total_bytes) || 0;
            if (!total) return 0;
            return Math.round(((parseFloat(item.bytes) || 0) / total) * 100);
        },
    },
};
</script>
